import React, { FC, useEffect, useState } from 'react';
import useTranslations from '@hooks/useTranslations';
import Collapsible from 'react-collapsible';
import Icon, { Icons } from '@components/Icon';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { AccessibleButton } from '@components/Button/AccessibleButton';
import { SummaryOptionsProps } from '@components/SummaryOptions/index';
import { ArrowIcon } from '@components/Carousel/ArrowsButtonGroup';
import CarOption from '@components/v2/CarOption';
import { useConfiguratorDuck } from '@hooks/useConfiguratorDuck';
import { theme } from '../../styles/theme';
import Spinner from '@components/Spinner';
import { IConfiguratorOption } from '../../redux/configurator/configurator.duck.interface';
import { DealService, getOptionsList } from '../../services';
import { getSessionIdCookie } from '@utils/Session.utils';
import { useDispatch, useSelector } from 'react-redux';
import { CarDetailsDuck } from '../../redux/carDetails/carDetails.duck';
import ConfiguratorDuck from '../../redux/configurator/configurator.duck';
import UIDuck from '../../redux/commonDucks/ui.duck';
import {
    CAR_OPTION_NAME_CLASS_NAME,
    TRIM_UPSELL_REDIRECT_PREVIOUS_LCDV_KEY,
} from '@components/v2/CarOption/CarOptionTemplate';
import { useJourneyType } from '@hooks/useJourneyType';
import { ICarOptionDetail } from '@components/Modal/ModalCarOptionDetail/ModalCarOptionDetailTemplate';
import FinanceWidgetDuck from 'src/redux/financeWidget/financeWidget.duck';
import { ITrimAttribute } from '../../interfaces/Car';
import { BACKEND_DEEPLINK_REDIRECT_URL, BRAND, LANGUAGE } from '../../constants/main';

export const WIDTH_OF_OPTION_CARD = 280;
const SPACE_WIDTH = 20;
const MOVE_WIDTH = 280;
const OPTION_WIDTH = 280;

const TRIM_UPSELL_CATEGORY_NAME = 'trimUpsell';
const TRIM_UPSELL_DETAIL_ATTRIBUTE_KEY = 'details';
const TRIM_UPSELL_DETAIL_LCDV_KEY = 'targetLCDV';
const TRIM_UPSELL_DETAIL_PRICE_KEY = 'price';

const SummaryOptionsTemplate: FC<SummaryOptionsProps> = ({ className }) => {
    const { t } = useTranslations();

    const dispatch = useDispatch();

    const { paymentJourneyType } = useJourneyType();

    const { selectedOptions } = useConfiguratorDuck();

    const { carDetails, currentDeal } = useCarDetailsDuck();

    const { carOptions } = useConfiguratorDuck();

    const isLoading = useSelector((state) => !!UIDuck.getOwnState(state).loading);

    const [isUpdating, setIsUpdating] = useState(false);
    const [isOpen, setIsOpen] = useState(true);
    const [hideArrows, setHideArrows] = useState(false);
    const [maxLeftMargin, setMaxLeftMargin] = useState(0);

    const [currentLeftMargin, _setCurrentLeftMargin] = useState(0);
    const currentLeftMarginRef = React.useRef(currentLeftMargin);
    function setCurrentLeftMargin(marginLeft: number) {
        currentLeftMarginRef.current = marginLeft; // Updates the ref
        _setCurrentLeftMargin(marginLeft);
    }

    let touchstartX = 0;
    let touchendX = 0;

    const checkDirection = () => {
        if (touchendX < touchstartX) handleMoveNext();
        if (touchendX > touchstartX) handleMoveBack();
    };

    const trimAttributes = currentDeal?.fullProductConfiguration?.trimAttributes;
    const filteredTrimUpsells = trimAttributes?.filter(
        (trimAttribute: ITrimAttribute) =>
            trimAttribute.category === TRIM_UPSELL_CATEGORY_NAME &&
            trimAttribute.attributeCode === TRIM_UPSELL_DETAIL_ATTRIBUTE_KEY
    );

    useEffect(() => {
        if (!isLoading && (carOptions?.length > 0 || filteredTrimUpsells?.length > 0)) {
            const mobileElement = document.getElementsByClassName('mobile-section')?.[0];
            const desktopElement = document.getElementsByClassName('desktop-section')?.[0];

            if (
                (desktopElement as HTMLElement)?.offsetWidth > 0 &&
                (desktopElement as HTMLElement)?.offsetWidth >
                    (carOptions?.length + filteredTrimUpsells?.length) * OPTION_WIDTH +
                        (carOptions?.length + filteredTrimUpsells?.length - 1) * SPACE_WIDTH
            )
                setHideArrows(true);

            if (
                (mobileElement as HTMLElement)?.offsetWidth > 0 &&
                (mobileElement as HTMLElement)?.offsetWidth >
                    (carOptions?.length + filteredTrimUpsells?.length) * OPTION_WIDTH +
                        (carOptions?.length + filteredTrimUpsells?.length - 1) * SPACE_WIDTH
            )
                setHideArrows(true);

            if ((mobileElement as HTMLElement)?.offsetWidth > 0) {
                const mobileSpacing = (carOptions?.length + filteredTrimUpsells?.length - 1) * SPACE_WIDTH + 20;
                setMaxLeftMargin(
                    (carOptions?.length + filteredTrimUpsells?.length) * WIDTH_OF_OPTION_CARD +
                        mobileSpacing -
                        (mobileElement as HTMLElement).offsetWidth
                );
            }

            if ((desktopElement as HTMLElement)?.offsetWidth > 0) {
                const desktopSpacing = (carOptions?.length + filteredTrimUpsells?.length - 1) * SPACE_WIDTH + 70;
                setMaxLeftMargin(
                    (carOptions?.length + filteredTrimUpsells?.length) * WIDTH_OF_OPTION_CARD +
                        desktopSpacing -
                        (desktopElement as HTMLElement).offsetWidth
                );
            }

            const optionsNames = Array.from(document.getElementsByClassName(CAR_OPTION_NAME_CLASS_NAME) || []);

            let maxHeight = 0;

            optionsNames?.map((optionName) => {
                if (maxHeight < (optionName as HTMLElement).offsetHeight) {
                    maxHeight = (optionName as HTMLElement).offsetHeight;
                }
            });

            if (maxHeight > 0) {
                optionsNames?.map((optionName) => ((optionName as HTMLElement).style.height = `${maxHeight}px`));
            }
        }
    }, [carOptions, isLoading]);

    useEffect(() => {
        if (!isLoading && maxLeftMargin > 0) {
            document?.getElementById('options')?.addEventListener('touchstart', (e) => {
                touchstartX = e.changedTouches[0].screenX;
            });

            document?.getElementById('options')?.addEventListener('touchend', (e) => {
                touchendX = e.changedTouches[0].screenX;
                checkDirection();
            });
        }
    }, [maxLeftMargin, isLoading]);

    useEffect(() => {
        const savedPreviousLCDV = localStorage.getItem(TRIM_UPSELL_REDIRECT_PREVIOUS_LCDV_KEY);

        if (localStorage.getItem(TRIM_UPSELL_REDIRECT_PREVIOUS_LCDV_KEY)) {
            localStorage.removeItem(TRIM_UPSELL_REDIRECT_PREVIOUS_LCDV_KEY);

            window.addEventListener('popstate', (event) => {
                event.preventDefault();
                window.location.href = `${BACKEND_DEEPLINK_REDIRECT_URL}/?brand=${BRAND}&language=${LANGUAGE}&version=${savedPreviousLCDV}&color=${
                    carDetails.exteriorColour.id
                }&trim=${carDetails.interiorColour.id}&page=summary${
                    selectedOptions ? `${selectedOptions?.map((opt, index) => `&opt${index + 1}=${opt}`)}` : ''
                }`;
            });
        }
    }, []);

    const handleMoveBack = () => {
        if (currentLeftMarginRef.current >= MOVE_WIDTH) {
            setCurrentLeftMargin(currentLeftMarginRef.current - MOVE_WIDTH);
        }

        if (currentLeftMarginRef.current < MOVE_WIDTH && currentLeftMarginRef.current > 0) {
            setCurrentLeftMargin(0);
        }
    };

    const handleMoveNext = () => {
        if (currentLeftMarginRef.current + MOVE_WIDTH <= maxLeftMargin) {
            setCurrentLeftMargin(currentLeftMarginRef.current + MOVE_WIDTH);
        }

        if (currentLeftMarginRef.current + MOVE_WIDTH > maxLeftMargin) {
            setCurrentLeftMargin(maxLeftMargin);
        }
    };

    const handleSelect = async (selectedOption: IConfiguratorOption) => {
        setIsUpdating(true);

        try {
            const isSelected = !!carDetails?.options?.find(
                (option: IConfiguratorOption) => option.id === selectedOption.id
            );

            const selectedOptions = isSelected
                ? carDetails?.options?.filter((option: IConfiguratorOption) => option.id !== selectedOption.id)
                : [...carDetails?.options, { ...selectedOption, upsellSelectedByCustomer: true }];

            const selectedOptionIds = selectedOptions?.map((option: IConfiguratorOption) => option.id) || [];

            const { data: dealData } = await DealService.updateDeal(
                {
                    selectedOptions,
                },
                getSessionIdCookie(),
                true
            );

            dispatch(CarDetailsDuck.setCurrentDeal(dealData?.deal));
            dispatch(CarDetailsDuck.setCurrentCarDetails(dealData?.deal?.fullProductConfiguration));

            if (dealData?.financeQuote) {
                dispatch(FinanceWidgetDuck.setFinanceQuote(dealData?.financeQuote));
            }

            const { data: optionsData } = await getOptionsList(
                dealData?.deal?.fullProductConfiguration?.externalId,
                selectedOptionIds?.length > 0 ? selectedOptionIds : null,
                paymentJourneyType
            );

            const responseOptions = optionsData?.options;

            dispatch(ConfiguratorDuck.setOptions(responseOptions));
            dispatch(
                ConfiguratorDuck.setAvailableOptions(
                    responseOptions
                        ?.filter((option: ICarOptionDetail) => option.upsell)
                        ?.filter(
                            (carOption: ICarOptionDetail) =>
                                !carDetails?.options
                                    // array of deeplink selected options
                                    ?.filter(
                                        (selectedOption: IConfiguratorOption) =>
                                            !selectedOption?.upsellSelectedByCustomer
                                    )
                                    // checking if available option was already selected by deeplink
                                    ?.find((selectedOption: IConfiguratorOption) => selectedOption.id === carOption.id)
                        )
                        ?.map(({ id }: { id: string }) => id)
                )
            );

            dispatch(
                ConfiguratorDuck.setSelectedOptions(
                    dealData?.deal?.fullProductConfiguration?.options?.map((option: IConfiguratorOption) => option.id)
                )
            );

            setIsUpdating(false);
        } catch {
            setIsUpdating(false);
        }
    };

    return (
        <div
            className={`${className} ${isOpen ? 'isOpen' : 'isClosed'} ${
                !isLoading && carOptions?.length === 0 && filteredTrimUpsells?.length === 0
                    ? 'withoutBorder'
                    : 'withBorder'
            }`}
        >
            {isLoading && (
                <div className="loadingWrapper">
                    <Spinner size={20} border={3} color={theme.colors.black} />
                </div>
            )}
            {!isLoading && (carOptions?.length > 0 || filteredTrimUpsells?.length > 0) && (
                <div>
                    <Collapsible
                        lazyRender={false}
                        open={isOpen}
                        trigger={
                            <AccessibleButton
                                tabIndex={0}
                                onClick={() => setIsOpen(!isOpen)}
                                className="titleWrapperButton"
                            >
                                <div className="titleWrapper">
                                    <span className="title">{t('summary.options.title')}</span>
                                    <Icon
                                        className={isOpen ? 'isOpen' : 'isClose'}
                                        name={isOpen ? Icons.ChevronUp : Icons.ChevronDown}
                                    />
                                </div>
                            </AccessibleButton>
                        }
                    >
                        <p
                            className="descriptionWrapper"
                            dangerouslySetInnerHTML={{ __html: t('summary.options.description') }}
                        />
                        <div className={`optionsWrapper ${isUpdating ? 'isLoading' : ''}`}>
                            {isUpdating && (
                                <div className="updatingWrapper">
                                    <Spinner size={20} border={3} color={theme.colors.black} />
                                </div>
                            )}
                            {!hideArrows && (
                                <button
                                    disabled={currentLeftMargin === 0}
                                    className="back-btn"
                                    onClick={handleMoveBack}
                                >
                                    <ArrowIcon
                                        width={10}
                                        altId="previousButton"
                                        altText={t('carGallery.previousButton.desc')}
                                    />
                                </button>
                            )}
                            {currentLeftMargin > 0 && <div className="left-blurry" />}
                            <div className="options" id="options" style={{ marginLeft: `-${currentLeftMargin}px` }}>
                                {carOptions
                                    .sort((a, b) => a?.upsellOrder - b?.upsellOrder)
                                    .map((carOption, index) => (
                                        <CarOption
                                            key={index}
                                            option={carOption}
                                            onChange={null}
                                            onClick={handleSelect}
                                            sendFullOptionAsParameterOnClick
                                        />
                                    ))}
                                {filteredTrimUpsells?.map((trimAttribute: ITrimAttribute, index: number) => (
                                    <CarOption
                                        key={`trimUpsell-${index}`}
                                        option={{
                                            id: trimAttributes?.find(
                                                (trimAttr: ITrimAttribute) =>
                                                    trimAttr.attributeCode === TRIM_UPSELL_DETAIL_LCDV_KEY
                                            )?.title,
                                            title: trimAttribute?.title,
                                            images: [
                                                {
                                                    url: trimAttribute?.imageURL,
                                                },
                                            ],
                                            description: trimAttribute?.description,
                                            formattedPrice: trimAttributes?.find(
                                                (trimAttr: ITrimAttribute) =>
                                                    trimAttr.attributeCode === TRIM_UPSELL_DETAIL_PRICE_KEY
                                            )?.title,
                                            selected: false,
                                            trimUpsell: true,
                                        }}
                                        onChange={null}
                                    />
                                ))}
                            </div>
                            {currentLeftMargin < maxLeftMargin && <div className="right-blurry" />}
                            {!hideArrows && (
                                <button
                                    disabled={currentLeftMargin >= maxLeftMargin}
                                    className="next-btn"
                                    onClick={handleMoveNext}
                                >
                                    <ArrowIcon
                                        width={10}
                                        altId="nextButton"
                                        altText={t('carGallery.nextButton.desc')}
                                    />
                                </button>
                            )}
                        </div>
                    </Collapsible>
                </div>
            )}
        </div>
    );
};

export default SummaryOptionsTemplate;
